import React, { useCallback, useRef, useState } from "react";
import { cn } from "../../lib/utils";

export interface EDSSwipeoutOption {
  name: string;
  icon: React.ReactNode;
  label: string;
  accent: "teal" | "blue" | "red" | "green";
  customColor?: string; // Optional custom hex color (e.g., "#00ff00")
}

export interface EDSSwipeoutProps {
  children: React.ReactNode;
  leftOptions?: EDSSwipeoutOption[];
  rightOptions?: EDSSwipeoutOption[];
  onOptionClick?: (optionName: string) => void;
  style?: React.CSSProperties;
  tabIndex?: number;
  className?: string;
}

export const EDSSwipeout: React.FC<EDSSwipeoutProps> = ({
  children,
  leftOptions = [],
  rightOptions = [],
  onOptionClick,
  style,
  tabIndex = 0,
  className = ""
}) => {
  // Debug logging removed to prevent excessive console output
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(
    null
  );
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(
    null
  );
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isDocked, setIsDocked] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const minSwipeDistance = 30; // Increased threshold for better sensitivity control
  const maxSwipeDistance = 80;
  const fullSwipeThreshold = maxSwipeDistance * 1.2; // Must swipe 20% beyond max to trigger full action (96px)

  // Minimum horizontal distance vs vertical distance ratio to consider it a horizontal swipe
  const minHorizontalRatio = 1.5; // Horizontal movement must be at least 1.5x the vertical movement

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      setTouchEnd(null);
      setTouchStart({
        x: e.targetTouches[0].clientX,
        y: e.targetTouches[0].clientY
      });

      // If currently docked and user starts a new swipe, reset docking
      if (isDocked) {
        setIsDocked(false);
        setSwipeOffset(0);
      }

      // Prevent touch events from bubbling to parent list
      e.stopPropagation();
    },
    [isDocked]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const currentTouch = {
        x: e.targetTouches[0].clientX,
        y: e.targetTouches[0].clientY
      };
      setTouchEnd(currentTouch);

      if (touchStart !== null) {
        const horizontalDistance = currentTouch.x - touchStart.x;
        const verticalDistance = Math.abs(currentTouch.y - touchStart.y);
        const horizontalDistanceAbs = Math.abs(horizontalDistance);

        // Only apply swipe offset if the gesture is primarily horizontal
        // This prevents vertical scrolling from triggering horizontal swipes
        if (horizontalDistanceAbs > verticalDistance * minHorizontalRatio) {
          const clampedDistance = Math.max(
            -maxSwipeDistance,
            Math.min(maxSwipeDistance, horizontalDistance)
          );
          setSwipeOffset(clampedDistance);

          // Only prevent propagation for horizontal swipes to allow vertical scrolling
          e.stopPropagation();
        } else {
          // For primarily vertical gestures, reset swipe offset and allow normal scrolling
          setSwipeOffset(0);
        }
      }
    },
    [touchStart, maxSwipeDistance, minHorizontalRatio]
  );

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) {
      // If no valid swipe, reset to current state
      if (!isDocked) {
        setSwipeOffset(0);
      }
      return;
    }

    const horizontalDistance = touchEnd.x - touchStart.x;
    const verticalDistance = Math.abs(touchEnd.y - touchStart.y);
    const horizontalDistanceAbs = Math.abs(horizontalDistance);

    // Only process as a horizontal swipe if it meets our criteria
    // This prevents vertical scrolling from being interpreted as horizontal swipes
    const isPrimaryHorizontal =
      horizontalDistanceAbs > verticalDistance * minHorizontalRatio;
    const meetsMinDistance = horizontalDistanceAbs > minSwipeDistance;

    if (!isPrimaryHorizontal || !meetsMinDistance) {
      // Reset for non-horizontal gestures or insufficient distance
      setTimeout(() => {
        setSwipeOffset(0);
        setIsDocked(false);
      }, 100);
      return;
    }

    const isRightSwipe = horizontalDistance > minSwipeDistance; // Swiping right (positive distance)
    const isLeftSwipe = horizontalDistance < -minSwipeDistance; // Swiping left (negative distance)
    const isFullSwipe = horizontalDistanceAbs > fullSwipeThreshold; // Must swipe beyond max distance to trigger

    // Check if we're currently docked and swiping in the opposite direction to reset
    const isUndockSwipe =
      isDocked &&
      ((swipeOffset > 0 && isLeftSwipe) || // Currently docked left (positive offset) and swiping left
        (swipeOffset < 0 && isRightSwipe)); // Currently docked right (negative offset) and swiping right

    // Determine which options to show based on swipe direction
    let targetOffset = 0;
    let shouldExecuteAction = false;
    let shouldDock = false;
    let shouldReset = false;

    // If this is an undock swipe, prioritize resetting over docking
    if (isUndockSwipe) {
      shouldReset = true;
    }
    // Right swipe (LEFT-TO-RIGHT) reveals left options
    else if (isRightSwipe && leftOptions.length > 0) {
      targetOffset = maxSwipeDistance;
      shouldExecuteAction = isFullSwipe;
      shouldDock = !isFullSwipe; // Dock if not a full swipe
    }
    // Left swipe (RIGHT-TO-LEFT) reveals right options
    else if (isLeftSwipe && rightOptions.length > 0) {
      targetOffset = -maxSwipeDistance;
      shouldExecuteAction = isFullSwipe;
      shouldDock = !isFullSwipe; // Dock if not a full swipe
    }

    // If full swipe, execute the action immediately
    if (shouldExecuteAction) {
      const targetOptions = isRightSwipe ? leftOptions : rightOptions;
      if (targetOptions.length > 0) {
        // Execute action immediately for better responsiveness
        onOptionClick?.(targetOptions[0].name);
        // Reset after a short delay to allow the action to process
        setTimeout(() => {
          setSwipeOffset(0);
          setIsDocked(false);
        }, 100);
        return;
      }
    }

    // If this is an undock swipe, reset to center position
    if (shouldReset) {
      setSwipeOffset(0);
      setIsDocked(false);
      return;
    }

    // If should dock, set the position and maintain it
    if (shouldDock) {
      setSwipeOffset(targetOffset);
      setIsDocked(true);
      return;
    }

    // Reset after animation (for small swipes that don't meet minimum distance)
    setTimeout(() => {
      setSwipeOffset(0);
      setIsDocked(false);
    }, 300);
  }, [
    touchStart,
    touchEnd,
    minSwipeDistance,
    maxSwipeDistance,
    fullSwipeThreshold,
    minHorizontalRatio,
    leftOptions,
    rightOptions,
    onOptionClick,
    isDocked,
    swipeOffset
  ]);

  const handleOptionClick = useCallback(
    (option: EDSSwipeoutOption, event?: React.MouseEvent) => {
      // Prevent event bubbling to parent elements
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      onOptionClick?.(option.name);
      // Reset position and docking state after option click
      setSwipeOffset(0);
      setIsDocked(false);
    },
    [onOptionClick]
  );

  const getAccentColor = (accent: string) => {
    switch (accent) {
      case "teal":
        return "var(--accent-color, #1fd4d4)";
      case "blue":
        return "var(--primary, #3b82f6)";
      case "red":
        return "var(--destructive, #ef4444)";
      case "green":
        return "var(--success, #10b981)";
      default:
        return "var(--accent-color, #1fd4d4)";
    }
  };

  const hasLeftOptions = leftOptions.length > 0;
  const hasRightOptions = rightOptions.length > 0;

  // Handle clicking on the main content to reset docking
  const handleContentClick = useCallback(() => {
    if (isDocked) {
      setIsDocked(false);
      setSwipeOffset(0);
    }
  }, [isDocked]);

  // Handle keyboard events for accessibility (S1082)
  const handleContentKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (
        isDocked &&
        (e.key === "Enter" || e.key === " " || e.key === "Escape")
      ) {
        e.preventDefault();
        setIsDocked(false);
        setSwipeOffset(0);
      }
    },
    [isDocked]
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        "eds-swipeout",
        "relative w-full h-full overflow-hidden shadow-none",
        "bg-panel-background",
        className
      )}
      style={style}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Left Options - Revealed when swiping LEFT-TO-RIGHT (positive swipeOffset) */}
      {hasLeftOptions && (
        <div
          className="eds-swipeout__left-options absolute h-full min-h-[60px] flex z-[1] transition-opacity duration-200 ease-out pointer-events-auto"
          style={{
            opacity:
              swipeOffset > 0 ? Math.min(1, swipeOffset / maxSwipeDistance) : 0
          }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          {leftOptions.map((option, index) => (
            <button
              key={`left-${option.name}-${index}`}
              className="eds-swipeout__option eds-swipeout__option--left relative flex items-center justify-center w-full h-full border-none rounded cursor-pointer transition-all duration-200 ease-in-out overflow-hidden"
              style={{
                backgroundColor:
                  option.customColor ?? getAccentColor(option.accent)
              }}
              onClick={(event: React.MouseEvent) =>
                handleOptionClick(option, event)
              }
            >
              <div className="eds-swipeout__option-overlay absolute inset-0 bg-white/50 z-0 pointer-events-none rounded-[inherit]" />
              <div className="eds-swipeout__option-content relative z-[1] flex flex-col items-center justify-center gap-1 p-2 text-center text-black">
                {option.icon}
                <span className="eds-swipeout__option-label text-xs font-bold leading-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[60px] text-black">
                  {option.label}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Right Options - Revealed when swiping RIGHT-TO-LEFT (negative swipeOffset) */}
      {hasRightOptions && (
        <div
          className="eds-swipeout__right-options absolute h-full min-h-[60px] flex z-[1] transition-opacity duration-200 ease-out pointer-events-auto"
          style={{
            opacity:
              swipeOffset < 0
                ? Math.min(1, Math.abs(swipeOffset) / maxSwipeDistance)
                : 0
          }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          {rightOptions.map((option, index) => (
            <button
              key={`right-${option.name}-${index}`}
              className="eds-swipeout__option eds-swipeout__option--right relative flex items-center justify-center w-full h-full border-none rounded cursor-pointer transition-all duration-200 ease-in-out overflow-hidden"
              style={{
                backgroundColor:
                  option.customColor ?? getAccentColor(option.accent)
              }}
              onClick={(event: React.MouseEvent) =>
                handleOptionClick(option, event)
              }
            >
              <div className="eds-swipeout__option-overlay absolute inset-0 bg-white/50 z-0 pointer-events-none rounded-[inherit]" />
              <div className="eds-swipeout__option-content relative z-[1] flex flex-col items-center justify-center gap-1 p-2 text-center text-black">
                {option.icon}
                <span className="eds-swipeout__option-label text-xs font-bold leading-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[60px] text-black">
                  {option.label}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Main Content */}
      <div
        ref={contentRef}
        className="eds-swipeout__content relative z-[2] w-full h-full bg-panel-background transition-transform duration-200 ease-out will-change-transform flex flex-col"
        style={{
          transform: `translateX(${swipeOffset}px)`
        }}
        tabIndex={tabIndex}
      >
        {children}
        {/* Close overlay button - native button for accessibility (S6819, S6848) */}
        {isDocked && (
          <button
            type="button"
            className="eds-swipeout__close-overlay absolute inset-0 z-[3] w-full h-full bg-transparent border-none cursor-pointer"
            aria-label="Close swipe options"
            onClick={handleContentClick}
            onKeyDown={handleContentKeyDown}
          />
        )}
      </div>
    </div>
  );
};
